import { Sparkles } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { SCENE_CARDS, taskSceneKeyOf } from "../catalog";
import { BRIEF_PURPOSES } from "../model/briefPurpose";
import { POSTER_HIDDEN_CARD_KEYS } from "../model/posterPurpose";
import type { SceneHub } from "../model/types";

/**
 * B6 场景筛 chips(三线列表共用):选项来自 scene-card catalog 按 hub 过滤 —— 场景名单一源在 catalog,
 * 这里不另立一份文案。值即 sceneKey,下推服务端 `?sceneKey=` 走 idx_media_task_scene 索引筛,
 * 不是前端只筛当前页(翻页外的命中会漏)。
 *
 * 只列**已上线且带 preset**的卡:soon 卡没有任务能打上它的标,列出来只会是永远空的筛选项。
 *
 * ★并且**跟卡墙同源**(2026-08-06 用户拍板):卡墙 08-05 最终版只摆 8 张(上架卖货 4 + 对外宣传 4),
 *   另外 6 张收进 `POSTER_HIDDEN_CARD_KEYS`;而这里原来只看 `enabled && preset`,于是资产页
 *   「我的作品 → 视觉舱」列出 14 个场景 —— 用户在筛选里读到墙上根本找不到的类目,两处对不上。
 *   收起的卡功能没删(长图卡内用途下拉仍切得到 10 种),但**首屏摆几张卡是同一个决定**,
 *   筛选项要跟着走。要放回来:把 key 从 POSTER_HIDDEN_CARD_KEYS 挪回 POSTER_PURPOSE_GROUPS,这里自动跟。
 */
export function SceneFilterChips({
  hub,
  value,
  onChange,
}: {
  hub: SceneHub;
  /** null = 全部(不筛)。 */
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const hidden = new Set<string>(POSTER_HIDDEN_CARD_KEYS);
  // ★视频例外(2026-08-10 四组卡):卡墙收成 4 组,但任务打标仍是 8 值 sceneKey
  //   (`video_<档.key>`,组只是选择层)—— 筛选项若跟卡墙走 4 组,4 个档的历史与新任务
  //   都会变成筛不出来。筛选面按**打标口径**列 8 档,不按卡墙口径。
  const options =
    hub === "video"
      ? BRIEF_PURPOSES.map((p) => ({ key: `video_${p.key}`, label: p.label }))
      : SCENE_CARDS.filter((c) => c.hub === hub && c.enabled && c.preset && !hidden.has(c.key)).map(
          (c) => ({
            // ★下推给服务端的是**打标 key**,不是卡 key —— 两者同名是常态、不同名是少数派
            //   (换衣:卡 key `tryon` / 打标 `ai_tryon`)。写 `c.key` 的话那张卡的筛选项
            //   一条都筛不出来,而且不报错(与视频那条例外同一种病:筛选面必须跟打标口径)。
            key: taskSceneKeyOf(c),
            label: c.title,
          })
        );
  if (options.length === 0) return null;

  return (
    <div
      className="flex w-fit flex-wrap items-center gap-1 rounded-lg border bg-muted/40 p-1"
      data-testid="scene-filter-chips"
    >
      <Sparkles className="ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <button
        type="button"
        onClick={() => onChange(null)}
        aria-pressed={value === null}
        className={cn(
          "rounded px-2 py-0.5 text-2xs transition-colors",
          value === null
            ? "bg-background font-medium text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        全部场景
      </button>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(value === o.key ? null : o.key)}
          aria-pressed={value === o.key}
          className={cn(
            "rounded px-2 py-0.5 text-2xs transition-colors",
            value === o.key
              ? "bg-background font-medium text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
